import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { APIError } from 'src/utils'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { QueryKeys } from '../../../enums/queryKeys'
import { deleteReceiptsBulk, DeleteReceiptsBulkPayload } from '../query-api'

interface UseReceiptsDeleteProps {
  onSuccess?: () => void
}

export const useReceiptsDelete = ({ onSuccess }: UseReceiptsDeleteProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutationOptions: UseMutationOptions<unknown, APIError, DeleteReceiptsBulkPayload, unknown> = {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.AttachmentsList)
      queryClient.invalidateQueries(QueryKeys.AttachmentsCount)

      notify({
        id: NotificationKeys.ReceiptsList,
        message: t('receipt.deleted'),
        variant: 'success',
      })
      onSuccess?.()
    },
    onError: (error) => {
      notify({
        id: NotificationKeys.ReceiptsList,
        message: error?.body?.errorMessage || t('receipt.deleted.error'),
        variant: 'error',
      })
    },
  }

  const deleteReceiptsMutation = useMutation(deleteReceiptsBulk, mutationOptions)

  const deleteReceipts = useCallback(
    (payload: DeleteReceiptsBulkPayload) => {
      deleteReceiptsMutation.mutate(payload)
    },
    [deleteReceiptsMutation],
  )

  return { isDeleting: deleteReceiptsMutation.isLoading, deleteReceipts }
}
